import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import { submitSurvey } from "../../services/ledgerService";

// SURVEYS is keyed by the recipient-friendly URL slug. `type` is the internal
// survey identifier the API stores (usage / churn) — never shown to recipients.
const SURVEYS = {
    "your-games": {
        type: "usage",
        title: "How you ran your games",
        intro: "You've hosted games on Poker Ledger before. I'd love to understand how you used it so I can make it more useful — it directly shapes what I build next.",
        ratingLabel: "How likely are you to host your next game on Poker Ledger?",
        commentLabel: "What would make Poker Ledger more useful for your games?",
    },
    comeback: {
        type: "churn",
        title: "Give it another look?",
        intro: "You signed up a while back but never got going. It just got a full redesign — and knowing what stopped you the first time helps me more than you'd think.",
        ratingLabel: "How likely are you to give Poker Ledger another try?",
        commentLabel: "What stopped you the first time?",
    },
};

const RATING_LOW = "Not likely";
const RATING_HIGH = "Very likely";

const Survey = () => {
    const { slug } = useParams();
    const [params] = useSearchParams();
    const email = params.get("e") || "";

    const config = SURVEYS[slug];

    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    if (!config) {
        return (
            <Container className="py-5">
                <Alert variant="warning">Survey not found.</Alert>
            </Container>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating < 1) return;
        setSubmitting(true);
        setError(null);
        try {
            await submitSurvey({ survey: config.type, email, rating, comment });
            setSubmitted(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container className="py-5">
            <Row>
                <Col md={8} lg={6} className="mx-auto">
                    <Card className="shadow-sm">
                        <Card.Body className="p-4">
                            {submitted ? (
                                <div className="text-center py-4">
                                    <h2 className="h4 mb-3">Thank you 🙏</h2>
                                    <p className="text-muted mb-0">
                                        That's genuinely helpful — I read every one of these. — Rocky
                                    </p>
                                </div>
                            ) : (
                                <Form onSubmit={handleSubmit}>
                                    <h1 className="h4 mb-2">{config.title}</h1>
                                    <p className="text-muted">{config.intro}</p>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-semibold">{config.ratingLabel}</Form.Label>
                                        <div className="d-flex flex-column align-items-center">
                                            <ButtonGroup>
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <Button
                                                        key={n}
                                                        type="button"
                                                        variant={rating === n ? "primary" : "outline-primary"}
                                                        onClick={() => setRating(n)}
                                                        aria-pressed={rating === n}
                                                    >
                                                        {n}
                                                    </Button>
                                                ))}
                                            </ButtonGroup>
                                            <div className="d-flex justify-content-between w-100 mt-1 px-1">
                                                <small className="text-muted">{RATING_LOW}</small>
                                                <small className="text-muted">{RATING_HIGH}</small>
                                            </div>
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-semibold">
                                            {config.commentLabel} <span className="text-muted fw-normal">(optional)</span>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            placeholder="Anything you want to share…"
                                        />
                                    </Form.Group>

                                    {error && <Alert variant="danger">{error}</Alert>}

                                    <div className="d-grid">
                                        <Button type="submit" variant="primary" disabled={rating < 1 || submitting}>
                                            {submitting ? <Spinner animation="border" size="sm" /> : "Submit"}
                                        </Button>
                                    </div>
                                    <p className="text-muted small text-center mt-3 mb-0">Takes about two minutes.</p>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Survey;
